import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  TextField,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { useForumStore, useAuthStore } from '../../stores/root_store';
import { AuthorName } from '../../components/AuthorName';
import type { ForumComment } from '../../types';

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

interface CommentItemProps {
  comment: ForumComment;
  sectionSlug: string;
  postSlug: string;
}

const CommentItem = observer(({ comment, sectionSlug, postSlug }: CommentItemProps) => {
  const forumStore = useForumStore();

  const handleLike = () => {
    forumStore.toggleLike(sectionSlug, postSlug, comment.slug);
  };

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AuthorName name={comment.author_name} userUuid={comment.user_uuid} variant="subtitle2" />
          <Typography variant="caption" color="text.secondary">
            {formatRelativeTime(comment.date_created)}
          </Typography>
          {comment.edited && (
            <Typography variant="caption" color="text.secondary">
              (edited)
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" onClick={handleLike}>
            {comment.user_has_liked ? (
              <ThumbUpIcon fontSize="small" color="primary" />
            ) : (
              <ThumbUpOutlinedIcon fontSize="small" />
            )}
          </IconButton>
          {comment.likes > 0 && (
            <Typography variant="body2" color="text.secondary">
              {comment.likes}
            </Typography>
          )}
        </Box>
      </Box>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
        {comment.body}
      </Typography>
    </Box>
  );
});

export const ForumPostPage = observer(() => {
  const { sectionSlug, postSlug } = useParams<{
    sectionSlug: string;
    postSlug: string;
  }>();
  const location = useLocation();
  const forumStore = useForumStore();
  const authStore = useAuthStore();

  const [commentBody, setCommentBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const post = forumStore.currentPost;
  const comments = forumStore.currentPostComments;
  const isManager = authStore.roles.includes('manager');

  const handleToggleSticky = async () => {
    if (!sectionSlug || !postSlug || !post) return;
    await forumStore.updatePost(sectionSlug, postSlug, { sticky: !post.sticky });
  };

  // Fetch post and comments on mount
  useEffect(() => {
    if (authStore.initialized && sectionSlug && postSlug) {
      forumStore.fetchPost(sectionSlug, postSlug);
      forumStore.fetchComments(sectionSlug, postSlug);
    }

    // Cleanup on unmount
    return () => {
      forumStore.clearCurrentPost();
    };
  }, [authStore.initialized, sectionSlug, postSlug, forumStore]);

  // Scroll to comment if hash is present
  useEffect(() => {
    if (location.hash && comments.length > 0) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [location.hash, comments]);

  const handleSubmitComment = async () => {
    if (!commentBody.trim() || !sectionSlug || !postSlug) return;

    setSubmitting(true);
    const result = await forumStore.createComment(sectionSlug, postSlug, commentBody);
    setSubmitting(false);

    if (result) {
      setCommentBody('');
    }
  };

  if (!sectionSlug || !postSlug) {
    return <Alert severity="error">Post not found</Alert>;
  }

  if (forumStore.postsLoading && !post) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (forumStore.postsError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {forumStore.postsError}
      </Alert>
    );
  }

  if (!post) {
    return <Alert severity="error">Post not found</Alert>;
  }

  if (post.archived) {
    return <Alert severity="info">This post has been archived</Alert>;
  }

  return (
    <Box>
      {/* Post Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {post.sticky && <PushPinIcon color="primary" />}
              <Typography variant="h5">{post.title}</Typography>
              {post.label && (
                <Chip
                  label={post.label}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
            {isManager && (
              <IconButton
                onClick={handleToggleSticky}
                title={post.sticky ? 'Unpin post' : 'Pin post'}
                color={post.sticky ? 'primary' : 'default'}
              >
                {post.sticky ? <PushPinIcon /> : <PushPinOutlinedIcon />}
              </IconButton>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="body2" color="text.secondary" component="span">
              by <AuthorName name={post.author_name} userUuid={post.user_uuid} />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDateTime(post.date_created)}
            </Typography>
            {post.edited && (
              <Typography variant="caption" color="text.secondary">
                (edited)
              </Typography>
            )}
          </Box>

          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {post.body}
          </Typography>
        </CardContent>
      </Card>

      {/* Comments Section */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Comments ({post.comment_count})
          </Typography>

          {/* Comments List */}
          {forumStore.commentsLoading && comments.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : comments.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No comments yet. Be the first to comment!
            </Typography>
          ) : (
            <>
              {comments.map((comment, index) => (
                <Box key={comment.uuid} id={`comment-${comment.uuid}`}>
                  <CommentItem
                    comment={comment}
                    sectionSlug={sectionSlug}
                    postSlug={postSlug}
                  />
                  {index < comments.length - 1 && <Divider />}
                </Box>
              ))}

              {forumStore.commentsHasMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    onClick={() =>
                      forumStore.fetchComments(sectionSlug, postSlug, true)
                    }
                    disabled={forumStore.commentsLoading}
                  >
                    {forumStore.commentsLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      'Load More Comments'
                    )}
                  </Button>
                </Box>
              )}
            </>
          )}

          <Divider sx={{ my: 2 }} />

          {/* New Comment Form */}
          {forumStore.saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {forumStore.saveError}
            </Alert>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Write a comment..."
            value={commentBody}
            onChange={(e) => setCommentBody(e.target.value)}
            disabled={submitting}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              variant="contained"
              onClick={handleSubmitComment}
              disabled={!commentBody.trim() || submitting}
            >
              {submitting ? <CircularProgress size={20} /> : 'Post Comment'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
});
